import React, { useEffect, useState } from 'react';
import { Plus, PenLine, ShieldAlert, Sparkles } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import AiNoteGeneratorModal from './AiNoteGeneratorModal';

const NOTE_STATUS_LABELS = { draft: 'Draft', ready_for_signature: 'Ready to sign', signed: 'Signed', voided: 'Voided' };
const NOTE_STATUS_BADGE = { draft: 'badge-warn', ready_for_signature: 'badge-warn', signed: 'badge-ok', voided: 'badge-neutral' };

function emptyForm() {
  return {
    id: null, enrollment_id: '', session_date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '',
    location: '', persons_present: '', goals_addressed: '', assessment: '', intervention: '', response: '', evaluation: '',
    si_status: '', hi_status: '', substance_concern: '', safety_concern: false, safety_note: '', plan: '',
  };
}

export default function PhpIndividualTherapy({ patient, user, notify, openAssessments, programType = 'php' }) {
  const canManage = hasCap(user, 'therapy.manage');
  const [notes, setNotes] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  async function quickAdmit() {
    setBusy(true);
    try {
      if (DEMO) {
        const demoEnr = { id: 'enr-' + Date.now(), program_type: programType || 'php', admitted_at: new Date().toISOString().slice(0, 10), status: 'active' };
        setEnrollments(prev => [demoEnr, ...prev]);
        setForm(f => ({ ...f, enrollment_id: demoEnr.id }));
        notify('Admitted to program (demo).');
        return;
      }
      await api(`/patients/${patient.id}/php/admit`, {
        method: 'POST',
        body: {
          program_type: programType || 'php',
          admitted_on: new Date().toISOString().slice(0, 10),
          reason_for_admission: 'Admitted for comprehensive behavioral health individual psychotherapy.',
          clinical_rationale: 'Meets criteria for active outpatient psychotherapy.',
        },
      });
      notify('Patient admitted to ' + (programType || 'php').toUpperCase() + '.');
      await load();
    } catch (err) {
      notify(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function load() {
    try {
      if (DEMO) {
        setNotes([]);
        const demoEnr = { id: 'enr-demo-1', program_type: programType || 'php', admitted_at: '2026-10-01', status: 'active' };
        setEnrollments([demoEnr]);
        setForm(f => ({ ...f, enrollment_id: f.enrollment_id || demoEnr.id }));
        return;
      }
      const [n, e] = await Promise.all([
        api(`/patients/${patient.id}/php/individual-therapy`).catch(() => ({ notes: [] })),
        api(`/patients/${patient.id}/php/enrollments`).catch(() => ({ enrollments: [] })),
      ]);
      const loadedNotes = n.notes || [];
      const loadedEnrollments = e.enrollments || [];
      setNotes(loadedNotes);
      setEnrollments(loadedEnrollments);
      if (loadedEnrollments.length > 0) {
        setForm(f => ({
          ...f,
          enrollment_id: f.enrollment_id || (loadedEnrollments.find(x => x.status === 'active')?.id || loadedEnrollments[0]?.id || ''),
        }));
      }
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) { notify('Individual therapy note saved (demo).'); setForm(emptyForm()); return; }
      if (form.id) await api(`/patients/${patient.id}/php/individual-therapy/${form.id}`, { method: 'PATCH', body: form });
      else await api(`/patients/${patient.id}/php/individual-therapy`, { method: 'POST', body: form });
      notify('Individual therapy note saved.');
      setForm(emptyForm());
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function sign(noteId) {
    setBusy(true);
    try {
      if (DEMO) { notify('Note signed (demo).'); return; }
      await api(`/patients/${patient.id}/php/individual-therapy/${noteId}/sign`, { method: 'POST', body: {} });
      notify('Note signed.');
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function addendum(noteId) {
    setBusy(true);
    try {
      if (DEMO) { notify('Addendum recorded (demo).'); return; }
      await api(`/patients/${patient.id}/php/individual-therapy/${noteId}/addendum`, { method: 'POST', body: {} });
      notify('Addendum recorded as a new version.');
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  function edit(n) {
    setForm({ id: n.id, enrollment_id: n.enrollment_id, session_date: n.session_date, start_time: n.start_time || '', end_time: n.end_time || '', location: n.location || '', persons_present: n.persons_present || '', goals_addressed: n.goals_addressed || '', assessment: n.assessment || '', intervention: n.intervention || '', response: n.response || '', evaluation: n.evaluation || '', si_status: n.si_status || '', hi_status: n.hi_status || '', substance_concern: n.substance_concern || '', safety_concern: n.safety_concern, safety_note: n.safety_note || '', plan: n.plan || '' });
  }

  const originals = notes.filter(n => !n.addendum_of);
  const addenda = notes.filter(n => n.addendum_of);

  return (
    <div>
      <div className="content-heading">
        <div>
          <h4>Individual therapy (A/I/R/E)</h4>
          <p>Assessment, intervention, response, and evaluation, with a risk/safety review and treatment-plan linkage. A signed note is immutable — corrections are addenda (new versions), never edits.</p>
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button type="button" className="button ghost" onClick={() => setShowAiModal(true)}>
              <Sparkles size={14} /> <span>AI note generator</span>
            </button>
            <button type="button" className="button primary" onClick={() => setForm(emptyForm())}>
              <Plus size={14} /> <span>New note</span>
            </button>
          </div>
        )}
      </div>

      {notes.length === 0 && <p className="empty-copy">No individual therapy notes yet.</p>}

      {originals.map(n => {
        const relatedAddenda = addenda.filter(a => a.addendum_of === n.id);
        return (
          <div key={n.id} className="php-plan">
            <div className="php-plan-head">
              <span className={`badge ${NOTE_STATUS_BADGE[n.status] || 'badge-neutral'}`}>{NOTE_STATUS_LABELS[n.status] || n.status}</span>
              <strong>{n.session_date}{[n.start_time, n.end_time].filter(Boolean).length ? ` · ${[n.start_time, n.end_time].filter(Boolean).join('–')}` : ''}</strong>
              {n.duration_minutes > 0 && <small>{n.duration_minutes} min</small>}
              {n.signed_at && <small>Signed {n.signed_at}{n.signer_title ? ` · ${n.signer_title}` : ''}</small>}
              <div className="spacer" />
              {canManage && (n.status === 'draft' || n.status === 'ready_for_signature') && (
                <>
                  <button type="button" className="button ghost" onClick={() => edit(n)}><span>Edit</span></button>
                  <button type="button" className="button ghost" disabled={busy} onClick={() => sign(n.id)}><span>Sign</span></button>
                </>
              )}
              {canManage && n.status === 'signed' && (
                <button type="button" className="button ghost" disabled={busy} onClick={() => addendum(n.id)}>
                  <PenLine size={14} /> <span>Addendum</span>
                </button>
              )}
            </div>
            <div className="php-plan-body" style={{ padding: '0 4px' }}>
              {n.assessment && <p><strong>Assessment.</strong> {n.assessment}</p>}
              {n.intervention && <p><strong>Intervention.</strong> {n.intervention}</p>}
              {n.response && <p><strong>Response.</strong> {n.response}</p>}
              {n.evaluation && <p><strong>Evaluation.</strong> {n.evaluation}</p>}
              {n.goals_addressed && <p><small>Goals addressed: {n.goals_addressed}</small></p>}
              {(n.si_status || n.hi_status || n.substance_concern) && <p className="muted"><small>Risk review — SI: {n.si_status || '—'} · HI: {n.hi_status || '—'} · Substance: {n.substance_concern || '—'}</small></p>}
              {n.safety_concern && <p style={{ color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><ShieldAlert size={14} /> <span>Safety concern documented{n.safety_note ? ` — ${n.safety_note}` : ''}</span></p>}
              {n.plan && <p><strong>Plan.</strong> {n.plan}</p>}
            </div>
            {relatedAddenda.map(a => (
              <div key={a.id} className="therapy-addendum">
                <div className="meta">Addendum v{a.version} — {a.created_at}</div>
                {a.assessment && <p><strong>Assessment.</strong> {a.assessment}</p>}
                {a.intervention && <p><strong>Intervention.</strong> {a.intervention}</p>}
                {a.response && <p><strong>Response.</strong> {a.response}</p>}
                {a.plan && <p><strong>Plan.</strong> {a.plan}</p>}
              </div>
            ))}
          </div>
        );
      })}

      {canManage && (
        <form className="php-form section-space" onSubmit={submit}>
          <div className="content-heading">
            <div><h4>{form.id ? 'Edit note' : 'New individual therapy note'}</h4></div>
            <button type="button" className="button ghost small" onClick={() => setShowAiModal(true)}>
              <Sparkles size={14} /> <span>AI Assist</span>
            </button>
          </div>
          <div className="form-grid">
            {enrollments.length === 0 && (
              <div className="inline-alert" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span>No active program enrollment found for this patient.</span>
                <button type="button" className="button small primary" onClick={quickAdmit} disabled={busy}>
                  <Plus size={13} /> <span>Admit to {(programType || 'php').toUpperCase()}</span>
                </button>
              </div>
            )}
            <label>Enrollment episode *
              <select value={form.enrollment_id} onChange={set('enrollment_id')} required>
                <option value="">— Select enrollment episode —</option>
                {enrollments.map(en => <option key={en.id} value={en.id}>{en.program_type?.toUpperCase()} · admitted {en.admitted_at} · {en.status}</option>)}
              </select>
            </label>
            <label>Session date<input type="date" value={form.session_date} onChange={set('session_date')} /></label>
            <label>Start time<input type="time" value={form.start_time} onChange={set('start_time')} /></label>
            <label>End time<input type="time" value={form.end_time} onChange={set('end_time')} /></label>
            <label className="span2">Location<input value={form.location} onChange={set('location')} maxLength={200} /></label>
            <label className="span2">Persons present<input value={form.persons_present} onChange={set('persons_present')} /></label>
            <label className="span2">Goals addressed<textarea rows={2} value={form.goals_addressed} onChange={set('goals_addressed')} maxLength={8000} /></label>
            <label className="span2">Assessment<textarea rows={3} value={form.assessment} onChange={set('assessment')} maxLength={8000} /></label>
            <label className="span2">Intervention<textarea rows={3} value={form.intervention} onChange={set('intervention')} maxLength={8000} /></label>
            <label className="span2">Response<textarea rows={3} value={form.response} onChange={set('response')} maxLength={8000} /></label>
            <label className="span2">Evaluation<textarea rows={3} value={form.evaluation} onChange={set('evaluation')} maxLength={8000} /></label>
          </div>
          <div className="content-heading section-space"><div><h4>Risk / safety review</h4><p>Documentation only — these fields never determine risk level, eligibility, or level of care.</p></div></div>
          <div className="form-grid">
            <label>Suicidal ideation (SI)<input value={form.si_status} onChange={set('si_status')} maxLength={30} placeholder="e.g. none / passive / active" /></label>
            <label>Homicidal ideation (HI)<input value={form.hi_status} onChange={set('hi_status')} maxLength={30} /></label>
            <label>Substance concern<input value={form.substance_concern} onChange={set('substance_concern')} maxLength={30} /></label>
            <label className="checkbox-label"><input type="checkbox" checked={form.safety_concern} onChange={set('safety_concern')} /> <span>Safety concern documented</span></label>
            {form.safety_concern && <label className="span2">Safety note<textarea rows={2} value={form.safety_note} onChange={set('safety_note')} maxLength={8000} /></label>}
            {hasCap(user, 'safety.manage') && form.safety_concern && (
              <div className="span2"><button type="button" className="button ghost" onClick={() => openAssessments && openAssessments()}><ShieldAlert size={14} /> <span>Start safety assessment</span></button></div>
            )}
            <label className="span2">Plan<textarea rows={2} value={form.plan} onChange={set('plan')} maxLength={8000} /></label>
          </div>
          <div className="php-plan-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button className="button primary" disabled={busy}><span>{busy ? 'Saving…' : form.id ? 'Update note' : 'Save note'}</span></button>
            {form.id && <button type="button" className="button ghost" onClick={() => setForm(emptyForm())}><span>Cancel edit</span></button>}
          </div>
        </form>
      )}

      {showAiModal && (
        <AiNoteGeneratorModal
          patient={patient}
          initialNoteType="individual_therapy_note"
          programType={programType}
          onClose={() => setShowAiModal(false)}
          notify={notify}
          onApply={draft => {
            setForm(f => ({
              ...f,
              assessment: draft.assessment || f.assessment,
              intervention: draft.intervention || f.intervention,
              response: draft.response || draft.individualized_response || f.response,
              evaluation: draft.evaluation || draft.progress || f.evaluation,
              goals_addressed: draft.goals_addressed || f.goals_addressed,
              plan: draft.plan || f.plan,
              location: draft.location || f.location,
              persons_present: draft.persons_present || f.persons_present,
              si_status: draft.si_status ?? f.si_status,
              hi_status: draft.hi_status ?? f.hi_status,
              substance_concern: draft.substance_concern ?? f.substance_concern,
              safety_concern: draft.safety_concern !== undefined ? draft.safety_concern : f.safety_concern,
              safety_note: draft.safety_note ?? f.safety_note,
            }));
          }}
        />
      )}
    </div>
  );
}
